import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { map, Observable } from 'rxjs';
import {
  LibrarySliceDeleteDocument,
  LibrarySliceDeleteMutation,
  LibrarySlicesDocument,
  LibrarySlicesQuery,
  LibrarySliceToggleDocument,
  LibrarySliceToggleMutation,
  LibrarySliceUpdateDocument,
  LibrarySliceUpdateMutation,
} from 'src/app/shared/graphql/generated/graphql';
import { DataSlice, SlicePreviewSeries } from 'src/app/state/library/library.model';

type ApiLibrarySlice = LibrarySlicesQuery['librarySlices'][number];

@Injectable({ providedIn: 'root' })
export class LibrarySliceApiService {

  constructor(private apollo: Apollo) {}

  fetchAll(): Observable<DataSlice[]> {
    return this.apollo
      .query<LibrarySlicesQuery>({
        query: LibrarySlicesDocument,
        fetchPolicy: 'network-only',
      })
      .pipe(map((result) => (result.data?.librarySlices ?? []).map((s) => this.toDataSlice(s))));
  }

  toggle(slice: DataSlice): Observable<DataSlice | null> {
    return this.apollo
      .mutate<LibrarySliceToggleMutation>({
        mutation: LibrarySliceToggleDocument,
        variables: { toggleLibrarySliceInput: this.toToggleInput(slice) },
      })
      .pipe(
        map((result) => {
          const apiSlice = result.data?.librarySliceToggle;
          return apiSlice ? this.toDataSlice(apiSlice) : null;
        }),
      );
  }

  update(
    id: string,
    patch: {
      fav?: boolean;
      title?: string;
      previewSeries?: SlicePreviewSeries[];
      datasetBuiltAt?: string;
    },
  ): Observable<DataSlice> {
    const { previewSeries, datasetBuiltAt, ...rest } = patch;
    return this.apollo
      .mutate<LibrarySliceUpdateMutation>({
        mutation: LibrarySliceUpdateDocument,
        variables: {
          updateLibrarySliceInput: {
            id,
            ...rest,
            ...(previewSeries
              ? { previewSeries: previewSeries.map((s) => this.toPreviewSeriesInput(s)) }
              : {}),
            ...(datasetBuiltAt ? { datasetBuiltAt } : {}),
          },
        },
      })
      .pipe(map((result) => this.toDataSlice(result.data!.librarySliceUpdate)));
  }

  delete(id: string): Observable<void> {
    return this.apollo
      .mutate<LibrarySliceDeleteMutation>({
        mutation: LibrarySliceDeleteDocument,
        variables: { id },
      })
      .pipe(map(() => undefined));
  }

  toDataSlice(apiSlice: ApiLibrarySlice): DataSlice {
    return {
      id: apiSlice.id,
      signature: apiSlice.signature,
      title: apiSlice.title,
      sub: apiSlice.sub,
      dashboardId: apiSlice.dashboardId,
      dashboardVariant: apiSlice.dashboardVariant ?? undefined,
      shellId: apiSlice.shellId ?? undefined,
      datasetName: apiSlice.datasetName,
      filterValues: apiSlice.filterValues ?? {},
      previewSeries: (apiSlice.previewSeries ?? []).map((s) => this.toPreviewSeries(s)),
      live: apiSlice.live,
      fav: apiSlice.fav,
      createdAt: this.toIsoString(apiSlice.createdAt),
      datasetBuiltAt: this.toIsoString(apiSlice.datasetBuiltAt),
    };
  }

  private toPreviewSeries(
    s: ApiLibrarySlice['previewSeries'][number],
  ): SlicePreviewSeries {
    return {
      code: s.code,
      name: s.name,
      shortName: s.shortName ?? undefined,
      vals: s.vals,
      years: s.years ?? undefined,
      lo: s.lo ?? undefined,
      hi: s.hi ?? undefined,
      cols: s.cols?.map((col) => ({ code: col.code, label: col.label })) ?? undefined,
      ranks: s.ranks ?? undefined,
      anon: s.anon ?? undefined,
      rankPool: s.rankPool ?? undefined,
      isSum: s.isSum ?? undefined,
      isFc: s.isFc ?? undefined,
    };
  }

  private toToggleInput(slice: DataSlice) {
    return {
      title: slice.title,
      sub: slice.sub,
      dashboardId: slice.dashboardId,
      dashboardVariant: slice.dashboardVariant,
      shellId: slice.shellId,
      datasetName: slice.datasetName,
      datasetBuiltAt: this.toBuiltAtInput(slice.datasetBuiltAt),
      filterValues: slice.filterValues,
      previewSeries: slice.previewSeries.map((s) => this.toPreviewSeriesInput(s)),
      live: slice.live,
      fav: slice.fav,
    };
  }

  private toPreviewSeriesInput(series: SlicePreviewSeries) {
    return {
      code: series.code,
      name: series.name,
      shortName: series.shortName,
      vals: series.vals,
      years: series.years,
      lo: series.lo,
      hi: series.hi,
      cols: series.cols,
      ranks: series.ranks,
      anon: series.anon,
      rankPool: series.rankPool,
      isSum: series.isSum,
      isFc: series.isFc,
    };
  }

  /**
   * The API only accepts a valid date. A dataset without built_at (or an old local slice)
   * falls back to the epoch, so the preview counts as stale once the dataset gets a build date.
   */
  private toBuiltAtInput(value: string | undefined): string {
    const date = new Date(value ?? '');
    return Number.isNaN(date.getTime()) ? new Date(0).toISOString() : date.toISOString();
  }

  private toIsoString(value: string): string {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toISOString();
  }
}
